import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Loader2, Calendar, Fuel, TrendingUp } from "lucide-react";
import api, { API } from "@/lib/api";

function fmtMoney(v, cur = "EUR") {
    if (v == null) return "—";
    try {
        return new Intl.NumberFormat("fr-FR", { style: "currency", currency: cur.toUpperCase() }).format(v);
    } catch {
        return `${v} ${cur}`;
    }
}

function pad2(n) {
    return String(n).padStart(2, "0");
}

function buildMonthOptions(n = 12) {
    const now = new Date();
    const out = [];
    for (let i = 0; i < n; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const start = new Date(d.getFullYear(), d.getMonth(), 1);
        const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        out.push({
            id: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`,
            label: d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
            startDate: `${start.getFullYear()}-${pad2(start.getMonth() + 1)}-${pad2(start.getDate())}`,
            endDate: `${end.getFullYear()}-${pad2(end.getMonth() + 1)}-${pad2(end.getDate())}`,
        });
    }
    return out;
}

export default function ConsolidatedReports() {
    const monthOptions = useMemo(() => buildMonthOptions(12), []);
    const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].id);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(null);
    const [minTx, setMinTx] = useState(1);
    const autoPickedRef = useRef(false);

    const range = useMemo(() => {
        const o = monthOptions.find((m) => m.id === selectedMonth) || monthOptions[0];
        return { startDate: o.startDate, endDate: o.endDate, label: o.label };
    }, [selectedMonth, monthOptions]);

    const loadPreview = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reports/consolidated/preview", {
                params: { start_date: range.startDate, end_date: range.endDate, min_transactions: minTx },
            });
            setPreview(data);
        } catch (e) {
            console.error("consolidated preview failed", e);
            toast.error(e?.response?.data?.detail || "Impossible de charger l'aperçu");
            setPreview(null);
        } finally {
            setLoading(false);
        }
    }, [range.startDate, range.endDate, minTx]);

    useEffect(() => {
        loadPreview();
    }, [loadPreview]);

    // On first load, if the current month has no merchants, auto-jump to the
    // most recent month that *does* have data. Saves the user from landing
    // on a blank "Juin 2026 — 0 marchands" screen and wondering why.
    useEffect(() => {
        if (autoPickedRef.current || loading || !preview) return;
        if (preview.merchants.length > 0) {
            autoPickedRef.current = true;
            return;
        }
        autoPickedRef.current = true;
        (async () => {
            for (const opt of monthOptions.slice(1)) {
                try {
                    const { data } = await api.get("/reports/consolidated/preview", {
                        params: { start_date: opt.startDate, end_date: opt.endDate, min_transactions: 1 },
                    });
                    if (data?.merchants?.length > 0) {
                        setMinTx(1);
                        setSelectedMonth(opt.id);
                        return;
                    }
                } catch (err) {
                    // Skip months that 4xx — keep scanning.
                    if (process.env.NODE_ENV !== "production") {
                        console.warn("consolidated auto-pick scan failed", opt.id, err);
                    }
                }
            }
        })();
    }, [preview, loading, monthOptions]);

    const download = async (merchant = "") => {
        const key = merchant || "__master__";
        setDownloading(key);
        try {
            const token = localStorage.getItem("tk_token");
            const params = new URLSearchParams({
                start_date: range.startDate,
                end_date: range.endDate,
                min_transactions: String(minTx),
            });
            if (merchant) params.set("merchant", merchant);
            const res = await fetch(`${API}/reports/consolidated/export?${params}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            });
            if (!res.ok) {
                let msg = `Erreur ${res.status}`;
                try {
                    const j = await res.json();
                    msg = j?.detail || msg;
                } catch (parseErr) {
                    console.debug("consolidated export error not JSON", parseErr);
                }
                throw new Error(msg);
            }
            const blob = await res.blob();
            const cd = res.headers.get("Content-Disposition") || "";
            const match = cd.match(/filename="?([^";]+)"?/i);
            const filename = match?.[1] || (merchant ? `recap-${merchant}.pdf` : "recap-master.pdf");
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(merchant ? `Récap ${merchant} téléchargé` : "Récap global téléchargé");
        } catch (e) {
            toast.error(e.message || "Échec du téléchargement");
        } finally {
            setDownloading(null);
        }
    };

    return (
        <div className="max-w-5xl mx-auto px-6 sm:px-8 space-y-8">
            <div className="tk-rise">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B]">Récapitulatifs marchands</p>
                <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D1D1F]">
                    Factures consolidées
                </h1>
                <p className="mt-2 text-sm text-[#86868B] max-w-2xl leading-relaxed">
                    L&apos;équivalent d&apos;une <strong className="text-[#1D1D1F]">carte carburant</strong> pour
                    chacun de vos marchands récurrents. <strong className="text-[#1D1D1F]">Uniquement les transactions justifiées</strong> (avec un ticket lié), consolidées en un seul document — prêt pour la compta.
                </p>
            </div>

            {/* Controls */}
            <section className="tk-card tk-rise tk-rise-1 p-6 sm:p-7 space-y-5" data-testid="consolidated-controls">
                <div className="grid sm:grid-cols-[1fr_auto_auto] gap-4 items-end">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#86868B] mb-2 flex items-center gap-1.5">
                            <Calendar className="w-3 h-3" /> Mois
                        </label>
                        <select
                            data-testid="consolidated-month"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            className="tk-input capitalize"
                        >
                            {monthOptions.map((o) => (
                                <option key={o.id} value={o.id} className="capitalize">
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#86868B] mb-2 block">
                            Min. transactions
                        </label>
                        <select
                            value={minTx}
                            onChange={(e) => setMinTx(Number(e.target.value))}
                            className="tk-input"
                            data-testid="consolidated-mintx"
                        >
                            <option value={1}>1+</option>
                            <option value={2}>2+</option>
                            <option value={3}>3+ (récurrents)</option>
                            <option value={5}>5+ (vraiment réguliers)</option>
                        </select>
                    </div>
                    <button
                        onClick={() => download("")}
                        disabled={!preview || preview.merchants.length === 0 || downloading === "__master__"}
                        className="tk-btn tk-btn-primary tk-cta-glow disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                        data-testid="consolidated-download-master"
                        title={(!preview || preview.merchants.length === 0) ? "Aucune transaction sur cette période — change de mois ou abaisse le seuil." : ""}
                    >
                        {downloading === "__master__" ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Fuel className="w-4 h-4" />
                        )}
                        PDF global (1 fichier)
                    </button>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-3 gap-3 pt-2">
                    <KPI label="Marchands" value={preview?.merchants.length ?? "—"} loading={loading} />
                    <KPI label="Transactions" value={preview?.total_count ?? "—"} loading={loading} />
                    <KPI
                        label="Total période"
                        value={preview ? fmtMoney(preview.grand_total) : "—"}
                        loading={loading}
                        accent="blue"
                    />
                </div>

                {!loading && preview && preview.merchants.length === 0 && (
                    <div
                        className="mt-2 p-4 rounded-2xl bg-[#FF9F0A]/8 border border-[#FF9F0A]/25 text-sm text-[#1D1D1F] leading-relaxed flex items-start gap-3"
                        data-testid="consolidated-zero-hint"
                    >
                        <TrendingUp className="w-5 h-5 text-[#B26A00] shrink-0 mt-0.5" />
                        <div>
                            <p className="font-semibold mb-1">Aucun ticket justifié sur <span className="capitalize">{range.label}</span>.</p>
                            <p className="text-[#86868B]">
                                {minTx > 1
                                    ? "Aucun marchand n'a au moins " + minTx + " transactions liées à un ticket ce mois-ci. Essaie « 1+ » ou un autre mois."
                                    : "Aucune transaction n'a encore de ticket lié sur cette période. Va sur « Transactions » pour vérifier les liens, ou choisis un autre mois."}
                                {" "}Le PDF s&apos;activera dès qu&apos;au moins un ticket justifié est trouvé.
                            </p>
                        </div>
                    </div>
                )}
            </section>

            {/* Merchant list */}
            <section className="tk-rise tk-rise-2 space-y-3" data-testid="consolidated-merchants">
                {loading ? (
                    <div className="tk-card p-10 text-center text-[#86868B]">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" /> Calcul…
                    </div>
                ) : !preview || preview.merchants.length === 0 ? (
                    <div className="tk-card p-12 text-center" data-testid="consolidated-empty">
                        <TrendingUp className="w-10 h-10 text-[#AEAEB2] mx-auto mb-3" />
                        <p className="text-[#86868B]">Aucun marchand récurrent sur cette période.</p>
                        <p className="mt-1 text-xs text-[#86868B]">
                            Essayez un autre mois ou abaissez le seuil « Min. transactions ».
                        </p>
                    </div>
                ) : (
                    <div className="tk-card divide-y divide-black/5 overflow-hidden">
                        <div className="px-6 py-4 flex items-center justify-between bg-[#F5F5F7]/50">
                            <p className="text-xs font-bold uppercase tracking-wider text-[#86868B]">
                                {preview.merchants.length} marchand{preview.merchants.length > 1 ? "s" : ""}
                                {" · "}
                                {preview.total_count} transaction{preview.total_count > 1 ? "s" : ""}
                            </p>
                            <p className="text-sm text-[#86868B]">Cliquez sur un marchand pour la facture PDF</p>
                        </div>
                        {preview.merchants.map((m) => (
                            <div
                                key={m.merchant}
                                className="flex items-center gap-4 p-5 hover:bg-black/[0.02] transition"
                                data-testid={`consolidated-row-${m.merchant}`}
                            >
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#007AFF]/15 to-[#34C759]/15 flex items-center justify-center shrink-0">
                                    <FileText className="w-4 h-4 text-[#1D1D1F]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-[#1D1D1F] truncate">
                                        {m.merchant}
                                    </p>
                                    <p className="text-xs text-[#86868B]">
                                        {m.transaction_count} transaction{m.transaction_count > 1 ? "s" : ""}
                                    </p>
                                </div>
                                <p className="font-semibold text-[#1D1D1F] tabular-nums">
                                    {fmtMoney(m.total_amount)}
                                </p>
                                <button
                                    onClick={() => download(m.merchant)}
                                    disabled={downloading === m.merchant}
                                    className="ml-2 w-10 h-10 rounded-full bg-[#F5F5F7] hover:bg-[#1D1D1F] hover:text-white text-[#1D1D1F] flex items-center justify-center transition disabled:opacity-50"
                                    title={`Télécharger le récap ${m.merchant}`}
                                    data-testid={`consolidated-download-${m.merchant}`}
                                >
                                    {downloading === m.merchant ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Download className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <div className="tk-card p-5 tk-rise tk-rise-3 flex items-start gap-3 text-sm text-[#86868B]">
                <Fuel className="w-5 h-5 text-[#34C759] shrink-0 mt-0.5" />
                <p>
                    <strong className="text-[#1D1D1F]">Comme une carte carburant.</strong> Chaque marchand
                    récurrent obtient sa facture mensuelle consolidée. Idéal pour votre comptable&nbsp;: au lieu
                    de 47 tickets en vrac, vous lui envoyez quelques PDF récapitulatifs propres.
                </p>
            </div>
        </div>
    );
}

function KPI({ label, value, accent, loading }) {
    const tone =
        accent === "blue" ? "text-[#007AFF]" : accent === "green" ? "text-[#34C759]" : "text-[#1D1D1F]";
    return (
        <div className="rounded-2xl bg-[#F5F5F7] border border-black/5 p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#86868B]">{label}</p>
            <p className={`mt-1 text-xl font-semibold ${tone}`}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : value}
            </p>
        </div>
    );
}
